import { Badge, Button, Card, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Table, TableBody, TableCell, TableHeader, TableHead, TableRow } from "randmarcomps";
import { CheckCircle2, ChevronLeft, ChevronRight, MinusCircle, Search } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { OpportunityEntry as ApiOpportunity } from "randmar-api-client";
import { useManufacturer } from "../../contexts/WithManufacturer";
import Path from "../../routes/Path";
import { Opportunity as LegacyOpportunity } from "../../stores/opportunities/Opportunity";
import { FormatDateFromString, FormatDateFromStringWithHours } from "../util/DateTimeUtil";

interface IProps {
  defaultRowsPerPage?: number;
  opportunities?: (LegacyOpportunity | ApiOpportunity)[];
}

const rowsPerPageOptions = [10, 20, 25, 50, 100] as const;

const isLegacy = (opp: LegacyOpportunity | ApiOpportunity): opp is LegacyOpportunity => "bidNumber" in opp;

const toApiOpportunity = (opp: LegacyOpportunity | ApiOpportunity): ApiOpportunity => {
  if (!isLegacy(opp)) return opp;
  return {
    BidNumber: opp.bidNumber,
    Opportunity: opp.opportunity,
    Description: opp.description,
    StartDate: opp.startDate,
    EndDate: opp.endDate,
    LastUpdate: opp.lastUpdate,
    Active: opp.active,
    DefaultResellers: opp.defaultResellers,
    CanBeSetAsDefault: opp.canBeSetAsDefault,
    BidPrice: opp.bidPrice,
    BidPriceType: opp.bidPriceType,
    RandmarSKU: opp.randmarSKU,
  };
};

const isOpportunityActive = (opp: ApiOpportunity) => {
  if (!opp.EndDate) return false;
  const parsedEndDate = new Date(opp.EndDate);
  if (Number.isNaN(parsedEndDate.getTime())) return false;
  return Boolean(opp.Active) && parsedEndDate >= new Date();
};

const getOpportunityStatus = (opp: ApiOpportunity): { variant: React.ComponentProps<typeof Badge>["variant"]; label: string; icon: React.ReactNode } => {
  const isActive = isOpportunityActive(opp);
  if (isActive) {
    return { variant: "success", label: "Active", icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> };
  }

  return { variant: "secondary", label: "Ended", icon: <MinusCircle className="h-4 w-4" aria-hidden="true" /> };
};

const OpportunitiesGrid: React.FC<IProps> = ({ opportunities: rawOpportunities, defaultRowsPerPage }) => {
  const { applicationId } = useManufacturer();
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage || 20);

  const opportunities = useMemo(() => (rawOpportunities || []).map(toApiOpportunity), [rawOpportunities]);

  const normalizedSearchValue = searchValue.trim().toLowerCase();
  const showSearch = opportunities.length > 3;

  const filteredOpportunities = useMemo(() => {
    if (!normalizedSearchValue) return opportunities;
    return opportunities.filter((row) => {
      const haystack = [
        row.BidNumber,
        row.Opportunity,
        row.Description,
        row.DefaultResellers,
        row.RandmarSKU,
        row.BidPrice?.toString(),
        row.BidPriceType,
        row.StartDate,
        row.EndDate,
        row.LastUpdate,
        row.Active?.toString(),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearchValue);
    });
  }, [normalizedSearchValue, opportunities]);

  React.useEffect(() => {
    setPage(0);
  }, [normalizedSearchValue]);

  const totalRows = filteredOpportunities.length;
  const safeRowsPerPage = Math.max(rowsPerPage, 1);
  const totalPages = Math.max(Math.ceil(totalRows / safeRowsPerPage), 1);
  const safePage = Math.min(page, totalPages - 1);
  const startRow = safePage * safeRowsPerPage;
  const endRow = Math.min(startRow + safeRowsPerPage, totalRows);

  const handlePreviousPage = useCallback(() => setPage((prev) => Math.max(prev - 1, 0)), []);
  const handleNextPage = useCallback((maxPages: number) => setPage((prev) => Math.min(prev + 1, maxPages - 1)), []);

  return (
    <Card className="w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      {showSearch ? (
        <div className="border-b border-gray-100 px-4 py-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <Input
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search opportunities…"
              className="h-11 rounded-lg pl-10"
              aria-label="Search opportunities"
            />
          </div>
        </div>
      ) : null}
      {filteredOpportunities.length === 0 ? (
        <div className="grid place-items-center p-10 text-center">
          <p className="text-[15px] font-medium text-slate-900">No matching opportunities</p>
          <p className="mt-1 text-[13px] text-slate-500">Try a different search or clear the filter.</p>
          <Button type="button" variant="outline" className="mt-4 rounded-xl" onClick={() => setSearchValue("")}>
            Clear search
          </Button>
        </div>
      ) : (
        <div className="w-full overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow className="h-12">
                <TableHead className="w-28 whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Status
                </TableHead>
                <TableHead className="whitespace-nowrap text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Number &amp; opportunity
                </TableHead>
                <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Start
                </TableHead>
                <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  End
                </TableHead>
                <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Last update
                </TableHead>
                <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                  Default resellers
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOpportunities.slice(startRow, endRow).map((row, index) => {
                const defaults = (row.DefaultResellers || "")
                  .split(",")
                  .map((value) => value.trim())
                  .filter(Boolean);
                const status = getOpportunityStatus(row);

                return (
                  <TableRow key={`${row.BidNumber ?? "opportunity"}-${index}`} className="h-14 transition-colors hover:bg-slate-50">
                    <TableCell className="text-center">
                      <Badge variant={status.variant} size="sm" icon={status.icon} className="mx-auto w-fit">
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="min-w-[240px] text-[15px]">
                      <Link to={Path.opportunity(applicationId, row.BidNumber || "")} className="font-medium text-slate-900 hover:underline">
                        <span className="font-semibold">{row.BidNumber}</span> {row.Opportunity}
                      </Link>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-center text-[15px] text-slate-700">
                      {FormatDateFromString(row.StartDate || undefined)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-center text-[15px] text-slate-700">
                      {FormatDateFromString(row.EndDate || undefined)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-center text-[15px] text-slate-700">
                      {FormatDateFromStringWithHours(row.LastUpdate || undefined)}
                    </TableCell>
                    <TableCell className="text-center">
                      {defaults.length === 0 ? (
                        <span className="text-[13px] text-slate-500">None</span>
                      ) : (
                        <div className="flex flex-wrap justify-center gap-1">
                          {defaults.map((value) => (
                            <span
                              key={value}
                              title={value}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[var(--ops-accent-muted)] text-xs font-semibold text-[var(--ops-accent)]"
                            >
                              {value.charAt(0).toUpperCase()}
                            </span>
                          ))}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {totalRows > safeRowsPerPage ? (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
          <p className="text-[13px] text-slate-500">
            Showing <span className="font-semibold text-slate-900">{startRow + 1}</span>–
            <span className="font-semibold text-slate-900">{endRow}</span> of{" "}
            <span className="font-semibold text-slate-900">{totalRows}</span>
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={String(safeRowsPerPage)}
              onValueChange={(value) => {
                setRowsPerPage(Number.parseInt(value, 10));
                setPage(0);
              }}
            >
              <SelectTrigger className="h-10 w-[140px] rounded-xl">
                <SelectValue placeholder="Rows" />
              </SelectTrigger>
              <SelectContent>
                {rowsPerPageOptions.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option} rows
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="button" variant="outline" size="sm" className="h-10 rounded-xl px-3" onClick={handlePreviousPage} disabled={safePage === 0}>
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 rounded-xl px-3"
              onClick={() => handleNextPage(totalPages)}
              disabled={safePage >= totalPages - 1}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
};

export default OpportunitiesGrid;
